import { setFulfilled, setPending, setRejected } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  StorylineAdminSetting,
  StorylineAdminSettingsUpdateSettingAction,
  StorylineAdminSettingsUpdateSettingRejectedAction,
  StorylinePerUserControlSettings,
  StorylinePerUserControlSettingsUpdateSettingAction,
} from '../../domains/storylineAdminSettings/actions.types';
import { StorylineDefaultNotificationDeliverySettingsUpdateFulfilledAction } from '../../domains/storylineAdminSettings/updateDefaultNotificationDeliverySettingsActions.types';
import { StorylineLoadAdminSettingsAction } from '../../domains/storylineLoadAdminSettings/actions.types';

import { StorylineAdminSettingsLifecycle } from './types';

const initialState: StorylineAdminSettingsLifecycle = {
  pending: false,
  fulfilled: undefined,
  rejected: undefined,
};

const updateFulfilledStorylineAdminSettingWithNewValue = (
  state: StorylineAdminSettingsLifecycle,
  payload: StorylineAdminSetting
) => ({
  ...state,
  fulfilled: { ...state.fulfilled, [payload.settingProp]: payload.settingValue },
});

const updateFulfilledStorylineAdminSettingWithPreviousValue = (
  state: StorylineAdminSettingsLifecycle,
  payload: StorylineAdminSettingsUpdateSettingRejectedAction['payload']
) => ({
  ...state,
  fulfilled: { ...state.fulfilled, [payload.settingPair.settingProp]: payload.prevSettingValue },
});

const updateFulfilledStorylinePerUserControlSettingWithNewValue = (
  state: StorylineAdminSettingsLifecycle,
  payload: StorylinePerUserControlSettings
) => ({
  ...state,
  fulfilled: {
    ...state.fulfilled,
    isStorylineOnlyForUsersInOfficeGroupEnabled: payload.isStorylineOnlyForUsersInOfficeGroupEnabled,
    storylineOnlyForUsersInOfficeGroup: payload.storylineOnlyForUsersInOfficeGroup,
  },
});

const updateFulfilledStorylineDefaultNotificationDeliverySettings = (
  state: StorylineAdminSettingsLifecycle,
  payload: StorylineDefaultNotificationDeliverySettingsUpdateFulfilledAction['payload']
) => ({
  ...state,
  fulfilled: { ...state.fulfilled, storylineDefaultNotificationDelivery: payload },
});

const reducer: Reducer<
  StorylineAdminSettingsLifecycle,
  | StorylineLoadAdminSettingsAction
  | StorylineAdminSettingsUpdateSettingAction
  | StorylinePerUserControlSettingsUpdateSettingAction
  | StorylineDefaultNotificationDeliverySettingsUpdateFulfilledAction
> = (state = initialState, action) => {
  switch (action.type) {
    case 'STORYLINE_ADMIN_SETTINGS.LOAD_FULFILLED':
      return setFulfilled(action.payload);
    case 'STORYLINE_ADMIN_SETTINGS.LOAD_PENDING':
      return setPending();
    case 'STORYLINE_ADMIN_SETTINGS.LOAD_REJECTED':
      return setRejected(action.payload.error);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_SETTING_VALUE_PENDING':
      return updateFulfilledStorylineAdminSettingWithNewValue(state, action.payload);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_SETTING_VALUE_REJECTED':
      return updateFulfilledStorylineAdminSettingWithPreviousValue(state, action.payload);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_SETTING_VALUE_FULFILLED':
      return updateFulfilledStorylineAdminSettingWithNewValue(state, action.payload);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_PER_USER_CONTROL_VALUES_FULFILLED':
      return updateFulfilledStorylinePerUserControlSettingWithNewValue(state, action.payload);
    case 'STORYLINE_ADMIN_SETTINGS.UPDATE_DEFAULT_NOTIFICATION_DELIVERY_SETTINGS_FULFILLED':
      return updateFulfilledStorylineDefaultNotificationDeliverySettings(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
